import React from "react";
import { Box, Typography, Grid, Container, Paper } from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PaymentsIcon from "@mui/icons-material/Payments";
import InsightsIcon from "@mui/icons-material/Insights";
import GroupsIcon from "@mui/icons-material/Groups";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import PaletteIcon from "@mui/icons-material/Palette";

/**
 * Six cards rather than three paragraphs of unadorned text.
 *
 * The old version listed three features as bare headings and body copy, which read as a document
 * rather than a product page and gave a visitor scanning the page nothing to anchor on. It also
 * omitted the two things organizers most want to know are handled (taking money, and not
 * double-booking a Scout) in favour of the generic "Easy to Use".
 *
 * Every claim here corresponds to something on the full feature list at /demo, so this stays a
 * summary of the product rather than a second, competing description of it.
 */
const features = [
  {
    icon: EventNoteIcon,
    title: "Schedule without conflicts",
    body: "Publish your class schedule with session times, capacity limits, age restrictions and prerequisites. Scouts cannot book two classes in the same hour, and a full class stops taking registrations.",
  },
  {
    icon: PaymentsIcon,
    title: "Collect payments online",
    body: "Families pay by card or PayPal at checkout. You do not need your own merchant account, and the proceeds are dispersed to your unit after the event.",
  },
  {
    icon: GroupsIcon,
    title: "Manage Scouts and counselors",
    body: "One parent account covers every Scout in the family. Assign counselors to the classes they are teaching and give them the roster and attendance for those classes only.",
  },
  {
    icon: InsightsIcon,
    title: "Reporting and exports",
    body: "Watch registrations and income in real time, then export your participant roster, counselor list and a full transaction ledger of every payment, refund and void.",
  },
  {
    icon: PhoneIphoneIcon,
    title: "Works on a phone",
    body: "Parents register from whatever device they have, and counselors take attendance on a phone on the day. There is nothing to install.",
  },
  {
    icon: PaletteIcon,
    title: "Looks like your event",
    body: "Your own logo, colour palette, custom FAQs, prerequisites, terms and conditions, flyer and email receipts, on your own badgereg.com address.",
  },
];

const FeaturesSection = () => {
  return (
    <Box sx={{ backgroundColor: "background.paper", py: { xs: 5, md: 8 } }}>
      <Container maxWidth="lg">
        <Typography
          variant="h4"
          component="h2"
          align="center"
          sx={{ fontWeight: "bold", mb: 1 }}
        >
          Everything a merit badge college needs
        </Typography>
        <Typography
          align="center"
          color="text.secondary"
          sx={{ mb: { xs: 3, md: 5 } }}
        >
          Purpose-built for the event, not a general-purpose ticketing tool.
        </Typography>
        <Grid container spacing={3}>
          {features.map(({ icon: Icon, title, body }) => (
            <Grid item xs={12} sm={6} md={4} key={title}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: 3,
                  backgroundColor: "background.default",
                  border: 1,
                  borderColor: "divider",
                }}
              >
                <Icon
                  aria-hidden="true"
                  sx={{ color: "primary.main", fontSize: 34, mb: 1.5 }}
                />
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ fontWeight: "bold", mb: 1 }}
                >
                  {title}
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {body}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default FeaturesSection;
